import type { CommissionEarningType } from "@/interfaces";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import api from "./api";

export interface CommissionApprovalConfig {
  configId: string;
  userId: string;
  userName: string;
  email: string;
  approvalLevel: number;
}

export interface CommissionApprovalCompanyGroup {
  companyId: string;
  companyName: string;
  approvers: CommissionApprovalConfig[];
}

export const getCommissionApprovalConfigs = async (): Promise<{ message: string; data: CommissionApprovalCompanyGroup[] }> => {
  const response = await api.get("/commission-approvals");
  return response.data;
};

export const createCommissionApprovalConfig = async (
  companyId: string,
  approvers: { userId: string; approvalLevel: number }[]
): Promise<{ message: string; data: any }> => {
  const response = await api.post("/commission-approvals", { companyId, approvers });
  return response.data;
};

export const deleteCommissionApprovalConfig = async (id: string): Promise<{ message: string }> => {
  const response = await api.delete(`/commission-approvals/${id}`);
  return response.data;
};

export const deleteCompanyCommissionApprovalConfigs = async (companyId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/commission-approvals/company/${companyId}`);
  return response.data;
};

export const checkIsApprover = async (companyId?: string): Promise<{ isApprover: boolean }> => {
  const response = await api.get("/commission-approvals/me/is-approver", { params: { companyId } });
  return response.data.data;
};

export const checkIsApproverforFinance = async (companyIds?: string[]): Promise<{ isApprover: boolean }> => {
  const response = await api.get("/commission-approvals/me/is-approver", { params: { companyId: companyIds } });
  return response.data.data;
};

export interface CommissionApprovalStep {
  approvalLevel: number;
  userId: string;
  userName: string;
  approved: boolean;
  approvedAt: string | null;
  comment: string | null;
}

// An earning in the logged-in approver's queue: waiting on them ("pending") or already approved by
// them ("approved"). approvalSteps is every configured level for the earning's company, in order.
export interface MyCommissionApprovalItem extends CommissionEarningType {
  myApprovalLevel: number;
  myApprovalState: "pending" | "approved";
  totalApprovalLevels: number;
  approvalSteps: CommissionApprovalStep[];
}

export interface MyCommissionApprovalsParams {
  tab: "pending" | "approved";
  page: number;
  limit: number;
  companyId?: string;
  clientId?: string;
  search?: string;
  // YYYY-MM-DD, invoice date range
  startDate?: string;
  endDate?: string;
  // YYYY-MM-DD, payment date range
  paymentStartDate?: string;
  paymentEndDate?: string;
}

export interface MyCommissionApprovalsResponse {
  data: MyCommissionApprovalItem[];
  total: number;
  page: number;
  limit: number;
  // Totals for both tabs under the current filters (tab labels)
  counts: { pending: number; approved: number };
  // Every client in the approver's queue, independent of filters (Client dropdown)
  clients: { id: string; name: string }[];
}

export const getMyCommissionApprovals = async (params: MyCommissionApprovalsParams): Promise<MyCommissionApprovalsResponse> => {
  const response = await api.get("/commission-approvals/me/earnings", { params });
  const body = response.data ?? {};
  return {
    data: body.data ?? [],
    total: body.total ?? 0,
    page: body.page ?? params.page,
    limit: body.limit ?? params.limit,
    counts: body.counts ?? { pending: 0, approved: 0 },
    clients: body.clients ?? [],
  };
};

export const approveCommissionAtMyLevel = async (
  earningId: string,
  comment?: string,
): Promise<{ message: string; data: { id: string; status: string } }> => {
  const response = await api.post(`/commission-approvals/earnings/${earningId}/approve`, { comment });
  return response.data;
};

export interface BulkApproveCommissionsResult {
  message: string;
  data: {
    approved: { id: string; status: string }[];
    alreadyApproved: string[];
    failed: { id: string; reason: string }[];
  };
}

// Approve several commissions at the logged-in approver's level with one shared comment.
export const bulkApproveCommissionsAtMyLevel = async (
  earningIds: string[],
  comment?: string,
): Promise<BulkApproveCommissionsResult> => {
  const response = await api.post("/commission-approvals/earnings/bulk-approve", { earningIds, comment });
  return response.data;
};

export const useGetMyCommissionApprovals = (params: MyCommissionApprovalsParams, enabled = true) =>
  useQuery({
    queryKey: ["my-commission-approvals", params],
    queryFn: () => getMyCommissionApprovals(params),
    enabled,
    // Keep the current page on screen while the next page / filter result loads.
    placeholderData: keepPreviousData,
  });

export default {
  getCommissionApprovalConfigs,
  createCommissionApprovalConfig,
  deleteCommissionApprovalConfig,
  deleteCompanyCommissionApprovalConfigs,
  checkIsApprover,
  getMyCommissionApprovals,
  approveCommissionAtMyLevel,
  bulkApproveCommissionsAtMyLevel,
};
